import type { MinimumFeaturedListViewModel, MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';

/**
 * 一覧マップのエリアラベル層（JCAT-5434）が使う「選択できる行き先」のビューモデル構築。
 *
 * - 都市点（destinations.json）はビルド時生成の静的アセット（scripts/gen-destination-points.mjs）。
 * - 「選択できる行き先」は行き先タグ（都市）単位。microCMS featured(destination) に
 *   id == 行き先タグ webPath のエントリがある行き先だけを選択可能とする。
 * - 並び順は microCMS の並び順を正本とする（管理画面の「コンテンツの並び替え」がそのまま反映される）。
 * - カードの画像・見出しは突き合わせた microCMS featured を使い、遷移先は行き先タグ LP /{webPath}。
 */

/**
 * 行き先タググループ（backend RPC `ListDestinationTagGroups` の 1 件）。
 *
 * 取得する composable ではなくここに置く。core は外側（composables）を知らない層なので、
 * 型を取得側に置くと内側から外側への依存になる（frontend-architecture.md「逆方向の依存は禁止」）。
 */
export interface FeaturedDestinationGroupViewModel {
  /** タググループ web_path */
  webPath: string;
  /** 表示名 */
  name: string;
  /** 公開体験件数（F-27） */
  experienceCount: number;
  /** 検索遷移用のメンバータグ web_path（?destinations= に展開） */
  tagWebPaths: string[];
}

/** destinations.json のエリア代表点。webPath はタググループ webPath。 */
export interface DestinationPoint {
  webPath: string;
  name: string;
  defaultLabel: boolean;
  x: number;
  y: number;
  latitude: number;
  longitude: number;
}

/** ラベルをドットのどちら側に置くか（近接都市のラベル衝突回避。生成スクリプトで手動調整）。 */
export type CityLabelDir = 'up' | 'down' | 'left' | 'right';

/** destinations.json の都市点（行き先タグ単位）。webPath は行き先タグ webPath。 */
export interface CityPoint {
  webPath: string;
  name: string;
  /** 所属タググループ webPath。 */
  groupWebPath: string;
  labelDir: CityLabelDir;
  x: number;
  y: number;
  latitude: number;
  longitude: number;
}

/** destinations.json のトップレベル形状。 */
export interface DestinationPointsJson {
  groups: DestinationPoint[];
  cities: CityPoint[];
}

/** 行き先タグ（都市）単位の選択可能な行き先。microCMS featured がある行き先のみ生成される。 */
export interface SelectableDestination {
  /** 行き先タグ webPath（例 'tokyo'）。カード遷移先 /{webPath}・microCMS featured の id と一致する。 */
  tagWebPath: string;
  /** カード見出し（microCMS featured の titleEn）。 */
  name: string;
  /** 親タググループ webPath。 */
  groupWebPath: string;
  /** 親グループの公開体験件数（F-27）。件数表示はグループ単位のまま。 */
  experienceCount: number;
  /** 突き合わせた microCMS featured(destination)。カード画像・見出しに使う。 */
  content: MinimumFeaturedViewModel;
  /** カードの説明文。microCMS の summary をプレーンテキスト化したもの。未入稿なら空文字。 */
  summary: string;
  /** この行き先タグ自身の都市点。destinations.json に無ければ undefined。 */
  cityPoint?: CityPoint;
  /** カードの遷移先＝行き先タグ LP（/{tagWebPath}）。 */
  to: string;
}

/**
 * microCMS のリッチテキスト（HTML）をカード表示用のプレーンテキストにする。
 *
 * summary は入稿によって `<p>` だけのこともあれば `<h1>` や `<br>` を含むこともあり、そのまま
 * v-html で出すとカードのタイポグラフィが崩れる（見出しが巨大化する等）。カードでは書式を落として
 * 1つの文章として扱い、行数は CSS の line-clamp で制御する。
 *
 * DOMParser は SSR に無いため使わず、文字列処理で済ませる（表示専用・HTML としては解釈しない）。
 */
export const toPlainText = (html: string | undefined): string => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ') // 改行タグは空白に潰す
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, ' ') // ブロック終端も空白扱い（単語が繋がらないように）
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * エリアラベルを優先して出す行き先タグ（PdM 決定「東京・京都は最初から表示」）。
 * microCMS の並び順に連動させると編集者の並び替えだけで固定ラベルが変わるため、静的に持つ。
 * ここに挙げたタグが選択不可（microCMS featured なし）の場合は、単にラベルが出なくなるだけで壊れない。
 */
export const ALWAYS_LABELED_TAG_WEB_PATHS = ['tokyo', 'kyoto'];

// 地方 catch-all タグ（「Other places (...)」）。地図上の都市点を持たず、選択対象から除外する（PdM 決定）。
const OTHERS_TAG_SUFFIX = '-others';

/**
 * RPC のタググループ（親・メンバータグ）・都市点・最新 microCMS featured を突き合わせ、
 * 「microCMS に featured がある行き先タグ（都市）」だけを選択可能な行き先として返す。
 *
 * - 突合キーは featured の id == 行き先タグ webPath（LP /{webPath} の解決キーと同一なので、
 *   カードのリンク先が必ず中身付きで開けることを同時に保証する）。
 * - `*-others`（地方 catch-all）は選択対象から除外する。
 * - featured が無い行き先タグは返さない（＝選択不可）。
 * - 返す順序は `destinationContentList`（＝microCMS の並び順）に揃える。走査は RPC のグループ順で
 *   行うが、それは「どのグループに紐づけるか」を決めるためだけで、表示順には使わない。
 */
export const buildSelectableDestinations = (
  destinationGroups: FeaturedDestinationGroupViewModel[],
  destinationContentList: MinimumFeaturedListViewModel,
  cityPoints: CityPoint[] = [],
): SelectableDestination[] => {
  const cityPointByWebPath = new Map(cityPoints.map((p) => [p.webPath, p]));
  // featured の id（== 行き先タグ webPath）→ content。type=destination のみ対象。
  const destinationContents = destinationContentList.filter((c) => c.type === 'destination');
  const contentByTagWebPath = new Map(destinationContents.map((c) => [c.id, c]));
  // 表示順の正本。microCMS の返却順（管理画面の並び替え順）をそのままカード順に使う。
  const orderByTagWebPath = new Map(destinationContents.map((c, i) => [c.id, i]));

  const result: SelectableDestination[] = [];
  const seen = new Set<string>();

  for (const group of destinationGroups) {
    for (const tagWebPath of group.tagWebPaths) {
      if (tagWebPath.endsWith(OTHERS_TAG_SUFFIX)) continue; // 地方 catch-all は選択対象外
      if (seen.has(tagWebPath)) continue; // 複数グループ所属タグの重複カードを防ぐ
      const content = contentByTagWebPath.get(tagWebPath);
      if (!content) continue; // microCMS に featured が無い＝選択不可
      seen.add(tagWebPath);
      result.push({
        tagWebPath,
        name: content.titleEn,
        summary: toPlainText(content.summary),
        groupWebPath: group.webPath,
        experienceCount: group.experienceCount,
        content,
        cityPoint: cityPointByWebPath.get(tagWebPath),
        to: `/${tagWebPath}/`,
      });
    }
  }
  // microCMS の並び順に整列する。走査順（＝RPC のグループ順・グループ内は web_path 昇順）のままだと
  // 「体験件数が多い地方から、その中はアルファベット順」になり、編集者が並びを制御できない。
  // すべての行き先は contentByTagWebPath にヒットしたものだけなので index は必ず引ける（?? は保険）。
  const orderOf = (d: SelectableDestination) => orderByTagWebPath.get(d.tagWebPath) ?? Number.MAX_SAFE_INTEGER;
  return result.sort((a, b) => orderOf(a) - orderOf(b));
};
